import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Col, Row, Modal } from "antd";
import { CertificateData, CertificateCategories } from "../constant/certificates";
import "./certificateGallery.css";

// ─── Certificate Card ──────────────────────────────────────────────────────────
const CertCard = ({ cert, index, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const isPdf = cert.type === "pdf";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 40, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.88, y: -20 }}
      transition={{ duration: 0.45, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8 }}
      onClick={() => onClick(cert)}
      className="cert-card"
      style={{ cursor: "pointer" }}
    >
      {/* Top accent bar */}
      <div className="cert-card-bar" style={{ background: cert.color }} />

      {/* Thumbnail */}
      <div className="cert-thumb">
        {isPdf ? (
          // PDF preview card
          <div className="cert-thumb-pdf" style={{ borderColor: cert.color }}>
            <motion.div
              className="cert-pdf-icon-wrap"
              style={{ background: cert.color }}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <i className="fa fa-file-pdf" style={{ fontSize: 32, color: "#fff" }} />
            </motion.div>
            <span className="cert-placeholder-text">PDF Certificate</span>
            <span style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4 }}>Click to view</span>
          </div>
        ) : !imgError ? (
          <img
            src={cert.imgURL}
            alt={cert.title}
            onError={() => setImgError(true)}
            className="cert-thumb-img"
          />
        ) : (
          <div className="cert-thumb-placeholder" style={{ borderColor: cert.color }}>
            <motion.img
              src={cert.icon}
              alt={cert.issuer}
              className="cert-placeholder-icon"
              animate={{ rotate: [0, -5, 5, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            <span className="cert-placeholder-text">Certificate Preview</span>
          </div>
        )}

        {/* Category badge */}
        <div className="cert-category-badge" style={{ background: cert.color }}>
          {cert.category}
        </div>
      </div>

      {/* Info */}
      <div className="cert-info">
        <div className="cert-title">{cert.title}</div>
        <div className="cert-issuer">
          {cert.issuer}
        </div>
        <div className="cert-meta">
          <span className="cert-date">
            <i className="fa fa-calendar" style={{ marginRight: 5 }} />
            {cert.date}
          </span>
          <motion.span className="cert-view-btn" whileHover={{ x: 4 }}>
            View →
          </motion.span>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Lightbox Modal ────────────────────────────────────────────────────────────
const CertModal = ({ cert, onClose }) => {
  const [imgError, setImgError] = useState(false);

  if (!cert) return null;
  const isPdf = cert.type === "pdf";

  return (
    <Modal
      open={!!cert}
      onCancel={onClose}
      footer={null}
      width={isPdf ? 900 : 760}
      centered
      title={null}
      closable={true}
    >
      <div className="cert-modal-content">
        {/* Header */}
        <div className="cert-modal-header" style={{ borderLeftColor: cert.color }}>
          <div>
            <div className="cert-modal-title">{cert.title}</div>
            <div className="cert-modal-issuer">{cert.issuer}</div>
          </div>
          <div className="cert-modal-badge" style={{ background: cert.color }}>
            {cert.category}
          </div>
        </div>

        {/* Certificate display */}
        <div className="cert-modal-img-wrap">
          {isPdf ? (
            <iframe
              src={cert.pdfURL}
              title={cert.title}
              className="cert-modal-pdf"
              style={{ width: "100%", height: 500, border: "none" }}
            />
          ) : !imgError ? (
            <img
              src={cert.imgURL}
              alt={cert.title}
              onError={() => setImgError(true)}
              className="cert-modal-img"
            />
          ) : (
            <div className="cert-modal-placeholder" style={{ borderColor: cert.color }}>
              <img src={cert.icon} alt="" style={{ width: 64, opacity: 0.4 }} onError={(e) => e.target.style.display = "none"} />
              <p style={{ color: "var(--text-muted)", marginTop: 16 }}>
                Add your certificate image to <code>public/certificates/</code>
              </p>
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="cert-modal-meta">
          <div className="cert-modal-meta-item">
            <span className="cert-modal-meta-label">Issued</span>
            <span className="cert-modal-meta-value">{cert.date}</span>
          </div>
          <div className="cert-modal-meta-item">
            <span className="cert-modal-meta-label">Credential ID</span>
            <span className="cert-modal-meta-value">{cert.credential}</span>
          </div>
          <div className="cert-modal-meta-item">
            <span className="cert-modal-meta-label">Category</span>
            <span className="cert-modal-meta-value">{cert.category}</span>
          </div>
          {cert.verifyURL && (
            <div className="cert-modal-meta-item">
              <span className="cert-modal-meta-label">Verify</span>
              <a
                href={cert.verifyURL}
                target="_blank"
                rel="noreferrer"
                className="cert-modal-meta-value"
                style={{ color: "var(--primary-color)", fontWeight: 700, fontSize: 13 }}
              >
                View on Coursera ↗
              </a>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

// ─── Main Gallery ──────────────────────────────────────────────────────────────
const CertificateGallery = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedCert, setSelectedCert] = useState(null);

  const filtered = activeCategory === "All"
    ? CertificateData
    : CertificateData.filter((c) => c.category === activeCategory);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        {/* Section header */}
        <div className="header" style={{ textAlign: "center", marginBottom: 16 }}>
          <i className="fa fa-certificate title-icon" />
          CERTIFICATES
        </div>
        <p style={{ textAlign: "center", color: "var(--text-muted)", marginBottom: 40, fontSize: 15 }}>
          Professional certifications and achievements
        </p>

        {/* Filter tabs */}
        <div className="cert-filter-bar">
          {CertificateCategories.map((cat) => (
            <motion.button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`cert-filter-btn ${activeCategory === cat ? "active" : ""}`}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.96 }}
              layout
            >
              {cat}
              {activeCategory === cat && (
                <motion.div
                  className="cert-filter-underline"
                  layoutId="cert-filter-underline"
                />
              )}
            </motion.button>
          ))}
        </div>

        {/* Grid */}
        <motion.div layout>
          <Row gutter={[28, 28]}>
            <AnimatePresence mode="popLayout">
              {filtered.map((cert, index) => (
                <Col xs={24} sm={12} lg={8} key={cert.id}>
                  <CertCard
                    cert={cert}
                    index={index}
                    onClick={setSelectedCert}
                  />
                </Col>
              ))}
            </AnimatePresence>
          </Row>
        </motion.div>

        {/* Count */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ textAlign: "center", color: "var(--text-muted)", marginTop: 32, fontSize: 13 }}
        >
          Showing {filtered.length} of {CertificateData.length} certificates
        </motion.p>
      </motion.div>

      {/* Lightbox */}
      <CertModal cert={selectedCert} onClose={() => setSelectedCert(null)} />
    </>
  );
};

export default CertificateGallery;
